'use client';

import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { extractJSON, run } from '@/lib/ai';
import { aspectSuggestions, aspectTitle } from '@/lib/prompts';
import { Btn } from '../Btn';
import { Nav, StepHead, StepWrap } from '../StepShell';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

const SAMPLE_INTERESTS = [
  "How companions are integrated into people's daily lives",
  'How and when people use their AI companion',
  'Whether the companion supplements or substitutes for human relationships',
  'The emotional impact of these companions',
  'Design features participants would like to see',
];

export function Step3Aspects({ data, setData, onBack, onNext }: Props) {
  const [draft, setDraft] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editingVal, setEditingVal] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [dynamicTitle, setDynamicTitle] = useState(
    'What are the main aspects you want to learn from this study?',
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!data.goal) return;
    run(aspectTitle(data.goal))
      .then((text) => {
        const q = text.trim().replace(/^["']|["']$/g, '');
        if (q) setDynamicTitle(q);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data.interests.length > 0 || !data.goal) return;
    // Loading state for an async fetch — kicking it off here is the standard
    // pattern; the React 19 set-state-in-effect rule is too strict for this case.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAiLoading(true);
    run(aspectSuggestions(data.goal))
      .then((text) => {
        const aspects = extractJSON<string[]>(text);
        if (aspects) setData({ ...data, interests: aspects.slice(0, 3) });
      })
      .catch(() => {})
      .finally(() => setAiLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    setData({ ...data, interests: [...data.interests, v] });
    setDraft('');
    inputRef.current?.focus();
  };

  const remove = (i: number) => {
    const arr = [...data.interests];
    arr.splice(i, 1);
    setData({ ...data, interests: arr });
  };

  const startEdit = (i: number) => {
    setEditingIdx(i);
    setEditingVal(data.interests[i]);
  };

  const commitEdit = () => {
    if (editingIdx === null) return;
    const arr = [...data.interests];
    if (editingVal.trim()) arr[editingIdx] = editingVal.trim();
    setData({ ...data, interests: arr });
    setEditingIdx(null);
    setEditingVal('');
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
    if (e.key === 'Tab' && !draft.trim() && data.interests.length === 0) {
      e.preventDefault();
      setData({ ...data, interests: SAMPLE_INTERESTS });
    }
  };

  return (
    <StepWrap>
      <StepHead
        label="Step 4 — Key Aspects"
        title={dynamicTitle}
        subtitle="List the core topics you want the interview to cover — not the actual questions, just the areas you want to make sure the conversation touches on. Write them in your own words. Order doesn't matter here — you'll have a chance to arrange the actual questions later."
      />

      {aiLoading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: T.terraLight,
            border: `1px solid ${T.terraMid}`,
            borderRadius: 10,
            padding: '14px 18px',
            fontSize: 14,
            color: T.espressoMid,
          }}
        >
          <span style={{ fontSize: 18, animation: 'spin 1s linear infinite', display: 'inline-block' }}>✦</span>
          <span>AI is suggesting aspects based on your study goal…</span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!aiLoading && data.interests.length > 0 && (
          <div style={{ fontSize: 12, color: T.espressoLight, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: T.terra }}>✦</span> AI suggested these — edit, remove, or add your own below
          </div>
        )}

        {data.interests.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              background: T.white,
              border: `1.5px solid ${editingIdx === i ? T.terra : T.creamBorder}`,
              borderRadius: 10,
              padding: '12px 16px',
              transition: 'border-color 0.15s',
            }}
          >
            <span style={{ color: T.terra, fontWeight: 600, marginTop: editingIdx === i ? 8 : 2, minWidth: 22, flexShrink: 0 }}>
              {i + 1}.
            </span>
            {editingIdx === i ? (
              <input
                autoFocus
                value={editingVal}
                onChange={(e) => setEditingVal(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitEdit();
                  }
                  if (e.key === 'Escape') setEditingIdx(null);
                }}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontFamily: 'inherit',
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: T.espresso,
                  outline: 'none',
                  padding: '4px 0',
                }}
              />
            ) : (
              <span
                onClick={() => startEdit(i)}
                title="Click to edit"
                style={{ flex: 1, fontSize: 15, lineHeight: 1.55, cursor: 'text', padding: '2px 0' }}
              >
                {item}
              </span>
            )}
            {editingIdx === i ? (
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitEdit();
                }}
                style={{
                  background: T.terra,
                  border: 'none',
                  cursor: 'pointer',
                  color: T.white,
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 6,
                  fontFamily: 'inherit',
                  fontWeight: 500,
                }}
              >
                Save
              </button>
            ) : (
              <button
                onClick={() => remove(i)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: T.espressoLight,
                  fontSize: 18,
                  lineHeight: 1,
                  padding: '0 2px',
                }}
                title="Remove"
              >
                ×
              </button>
            )}
          </div>
        ))}

        <div
          style={{
            display: 'flex',
            gap: 10,
            border: `1.5px dashed ${T.creamBorder}`,
            borderRadius: 10,
            padding: '10px 14px',
            background: T.cream,
            position: 'relative',
          }}
        >
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder="Add an aspect… press Enter to add"
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: 15,
              color: T.espresso,
              outline: 'none',
            }}
          />
          {inputFocused && !draft.trim() && data.interests.length === 0 && (
            <span
              style={{
                fontSize: 11,
                color: T.espressoLight,
                background: T.creamMid,
                borderRadius: 4,
                padding: '2px 7px',
                alignSelf: 'center',
                flexShrink: 0,
                border: `1px solid ${T.creamBorder}`,
              }}
            >
              Tab ↹ to fill examples
            </span>
          )}
          <Btn small onClick={add} disabled={!draft.trim()}>
            Add
          </Btn>
        </div>
      </div>

      {data.interests.length === 0 && (
        <div style={{ fontSize: 13, color: T.espressoLight, fontStyle: 'italic', marginTop: -8 }}>
          Try adding themes like: &ldquo;Daily usage patterns&rdquo;, &ldquo;Emotional impact&rdquo;, &ldquo;Feature
          wishes&rdquo;…
        </div>
      )}

      <Nav onBack={onBack} onNext={onNext} nextDisabled={data.interests.length === 0} />
    </StepWrap>
  );
}
